import { supabase } from "@/integrations/supabase/client";
import { CategoryInfo, UserCategory } from "@/types";
import logger from "@/utils/logger";
import { networkService } from '@/services/network.service';

/**
 * Get all categories for the current user
 * @returns Array of user categories
 */
export const getUserCategories = async (): Promise<UserCategory[]> => {
  if (!networkService.isOnline()) {
    console.warn("[getUserCategories] Offline: salto il fetch da Supabase");
    return [];
  }

  try {
    const { data: userData, error } = await supabase.auth.getUser();
    
    if (error || !userData.user) {
      console.error("Error getting user:", error);
      return [];
    }
    
    const userId = userData.user.id;

    const { data: userCategories, error: categoriesError } = await supabase
      .from('user_categories')
      .select('*')
      .eq('user_id', userId)
      .order('position', { ascending: true });
    
    if (categoriesError) {
      console.error("Error fetching user categories:", categoriesError);
      return [];
    }
    
    return userCategories.map(cat => ({
      id: cat.id,
      name: cat.name || '',
      color: cat.color || '#f0f0f0',
      icon: cat.icon || '📦',
      userId: cat.user_id,
      position: cat.position,
      textColor: cat.text_color || '#000000',
      categoryId: cat.id,
      visibile: cat.visibile !== false
    }));
  } catch (err) {
    console.error("Error in getUserCategories:", err);
    return [];
  }
};
/**
 * Get only visible categories for the current user
 * @returns Array of visible user categories
 */
export const getVisibleUserCategories = async (): Promise<UserCategory[]> => {
  try {
    const allCategories = await getUserCategories();
    return allCategories.filter(cat => cat.visibile !== false);
  } catch (err) {
    console.error("Error in getVisibleUserCategories:", err);
    return [];
  }
};

// Aggiorna la posizione in shopping_categories in base all'ordinamento definito nello shop
export const updateShoppingCategoriesFromShopOrdering = async (
  listId: string,
  shopId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc("update_category_positions_for_list", {
      input_list_id: listId,
      input_shop_id: shopId
    });

    if (error) {
      logger.logError("categories.service","update_category_positions_for_list",{additionalInfo:error});
      throw error;
    }

    return true;
  } catch (err) {
    logger.logError("categories.service","updateShoppingCategoriesFromShopOrdering",{additionalInfo:err});
    throw err;
  }
};


export const getShoppingCategoriesForList = async (listId: string) => {
  const { data, error } = await supabase
    .from("shopping_categories")
    .select("category_id, position, user_categories(*)")
    .eq("list_id", listId)
    .order("position", { ascending: true });

  if (error) throw error;

  return data.map((row) => ({
    id: row.category_id,
    position: row.position,
    ...row.user_categories
  }));
};

export const createUserCategory = async (data: {
  name: string;
  icon: string;
  color: string;
  text_color: string;
}) => {
  // Get the current user ID
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    throw userError || new Error("User not authenticated");
  }
  const userId = userData.user.id;

  // Check if a category with the same name already exists (case-insensitive)
  const { data: existingCategory, error: existingError } = await supabase
    .from("user_categories")
    .select("*")
    .eq("user_id", userId)
    .ilike("name", data.name)
    .maybeSingle();

  if (existingError) {
    throw existingError;
  }

  if (existingCategory) {
    return existingCategory;
  }

  // Add user_id to the data object
  const insertData = { ...data, user_id: userId };

  // Generate a UUID for the id (since we no longer use text-based IDs)
  const { data: result, error } = await supabase
    .from("user_categories")
    .insert([insertData])
    .select()
    .single();

  if (error) {
    throw error;
  }

  return result;
};


/**
 * Update a user category
 * @param categoryId ID of the category to update
 * @param categoryData New category data
 * @returns Success status
 */
export const updateUserCategory = async (
  categoryId: string,
  categoryData: Partial<CategoryInfo>
): Promise<boolean> => {
  try {
    // Ensure valid UUID
    if (!isValidUUID(categoryId)) {
      console.error("Invalid UUID format for category ID:", categoryId);
      return false;
    }

    // Costruisci payload pulito (evita `undefined` e converte `textColor` -> `text_color`)
    const updateData: any = {
      ...(categoryData.name !== undefined && { name: categoryData.name }),
      ...(categoryData.icon !== undefined && { icon: categoryData.icon }),
      ...(categoryData.color !== undefined && { color: categoryData.color }),
      ...(categoryData.textColor !== undefined && { text_color: categoryData.textColor }) // attenzione qui
    };

    // Log per debugging
    logger.log("🚀 PATCH payload per Supabase:", updateData);

    const { error } = await supabase
      .from("user_categories")
      .update(updateData)
      .eq("id", categoryId);

    if (error) {
      console.error("❌ Supabase update error:", error);
      return false;
    }

    return true;
  } catch (err) {
    console.error("❌ Errore inatteso in updateUserCategory:", err);
    return false;
  }
};


/**
 * Update the order of categories
 * @param categoryIds Array of category IDs in the new order
 * @returns Success status
 */
export const updateCategoriesOrder = async (categoryIds: string[]): Promise<boolean> => {
  try {
    // Create an array of update operations
    const updatePromises = categoryIds.map((id, index) => {
      // Skip any non-UUID values
      if (!isValidUUID(id)) {
        console.error("Invalid UUID format for category ID:", id);
        return Promise.resolve({ error: null });
      }
      
      return supabase
        .from('user_categories')
        .update({ position: index })
        .eq('id', id);
    });
    
    // Run all updates in parallel
    const results = await Promise.all(updatePromises);
    
    // Check if any update failed
    const anyError = results.some(result => result.error);
    if (anyError) {
      console.error("Error updating category positions");
      return false;
    }
    
    return true;
  } catch (err) {
    console.error("Error updating category positions:", err);
    return false;
  }
};

/**
 * Update the position of a single category
 * @param categoryId ID of the category to update
 * @param position New position value
 * @returns Success status
 */
export const updateCategoryPosition = async (data: { categoryId: string; position: number }): Promise<boolean> => {
  try {
    // Ensure we have a valid UUID for categoryId
    if (!isValidUUID(data.categoryId)) {
      console.error("Invalid UUID format for category ID:", data.categoryId);
      return false;
    }
    
    const { error } = await supabase
      .from('user_categories')
      .update({ position: data.position })
      .eq('id', data.categoryId);
    
    if (error) {
      console.error("Error updating category position:", error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error("Error updating category position:", err);
    return false;
  }
};

/**
 * Delete a user category
 * @param categoryId ID of the category to delete
 * @returns Success status
 */
export const deleteUserCategory = async (categoryId: string): Promise<boolean> => {
  try {
    // Ensure we have a valid UUID for categoryId
    if (!isValidUUID(categoryId)) {
      console.error("Invalid UUID format for category ID:", categoryId);
      return false;
    }
    
    const { error } = await supabase
      .from('user_categories')
      .delete()
      .eq('id', categoryId);
    
    if (error) {
      console.error('Error deleting category:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Error deleting category:', err);
    return false;
  }
};

/**
 * Update the visibility of a category
 * @param data Object containing categoryId and visible status
 * @returns Success status
 */
export const updateCategoryVisibility = async (data: { 
  categoryId: string; 
  visible: boolean 
}): Promise<boolean> => {
  try {
    // Ensure we have a valid UUID for categoryId
    if (!isValidUUID(data.categoryId)) {
      console.error("Invalid UUID format for category ID:", data.categoryId);
      return false;
    }
    
    const { error } = await supabase
      .from('user_categories')
      .update({ visibile: data.visible })
      .eq('id', data.categoryId);
    
    if (error) {
      console.error("Error updating category visibility:", error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error("Error updating category visibility:", err);
    return false;
  }
};

/**
 * Helper function to check if a string is a valid UUID
 */
function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}
